import { datadogLogs } from '@datadog/browser-logs';
import Api from '@silbo/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { liveQueryClient } from '../live-query-client';
import useMemoizedQuery from './useMemoizedQuery';

const useLiveQuery = <T extends Parse.Object>(query: Parse.Query<T>, objectId?: string) => {
  const memoizedQuery = useMemoizedQuery<T>(query, objectId);
  const queryKey = useMemo(() => [memoizedQuery.className, memoizedQuery.toJSON()], [memoizedQuery]);

  const include = useMemo(() => memoizedQuery._include ?? [], [memoizedQuery._include]);

  const queryClient = useQueryClient();

  useEffect(() => {
    let subscription: Parse.LiveQuerySubscription | undefined;

    const init = async () => {
      const userSessionToken = Api.User.current()?.getSessionToken();
      if (!userSessionToken) {
        return;
      }
      subscription = liveQueryClient.subscribe(memoizedQuery, userSessionToken);
      const handleUpdate = async (object: Parse.Object) => {
        let entity = object;
        if (include.length > 0 && object.id) {
          try {
            entity = await new Api.Query<T>(memoizedQuery.className).include(include).get(object.id);
          } catch (error) {
            // Le re-fetch peut échouer (réseau, session expirée, …) : on garde l'entrée existante du cache
            // plutôt que de la remplacer par l'objet LiveQuery brut, sans les `include`.
            datadogLogs.logger.warn('useLiveQuery: failed to fetch entity with includes', {
              className: memoizedQuery.className,
              objectId: object.id,
              error,
            });
            return;
          }
        }
        queryClient.setQueriesData<T[]>({ queryKey }, (prev) => {
          if (!prev) {
            return [entity as T];
          }
          const existingEntityIndex = prev?.findIndex((item) => item.id === entity.id);
          if (existingEntityIndex !== -1) {
            const newPrev = [...prev];
            newPrev[existingEntityIndex] = entity as T;
            return newPrev;
          }
          return [...prev, entity as T];
        });
      };
      const handleDelete = (object: Parse.Object) => {
        queryClient.setQueriesData<T[]>({ queryKey }, (prev) => {
          return prev?.filter((item) => item.id !== object.id);
        });
      };

      if (subscription) {
        subscription.on('enter', handleUpdate);
        subscription.on('create', handleUpdate);
        subscription.on('update', handleUpdate);
        subscription.on('delete', handleDelete);
        subscription.on('leave', handleDelete);
      }
    };

    init();

    return () => {
      memoizedQuery.cancel();
      if (subscription) {
        liveQueryClient.unsubscribe(subscription);
      }
      queryClient.cancelQueries({ queryKey, exact: true });
    };
  }, [memoizedQuery, include, queryClient, queryKey]);
};

export default useLiveQuery;
